import { AppWindow, RefreshCw } from "lucide-react";

import { deriveAppDisplayName } from "@domain/studio-app";

export interface StudioPendingProps {
  /** The app directory from the URL — named but not (yet) on disk. */
  appDir: string;
  /** Re-probe the vault for the app's first files. */
  onRefresh?: () => void;
}

/**
 * The detail route's pending pane (tri-state branch for NOT_FOUND, studio
 * spec §4.4): the appDir names an app whose first files haven't landed in the
 * vault yet — commonly mid-build. Same ghost-card visual language as the
 * library's empty state; refresh is manual (no timers, spec §1.2).
 */
export function StudioPending({ appDir, onRefresh }: StudioPendingProps) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16 text-center">
      {/* Ghost app card — the shape of an app, waiting to exist */}
      <div className="relative rotate-[-2deg] opacity-70" aria-hidden>
        <div className="w-[220px] overflow-hidden rounded-2xl border border-dashed border-home-ink/20 bg-white/60 dark:border-white/20 dark:bg-white/[0.04]">
          <div className="h-20 border-b border-dashed border-home-ink/15 bg-home-ink/[0.04] dark:border-white/15 dark:bg-white/[0.06]" />
          <div className="flex items-center justify-between p-3">
            <span className="h-2 w-20 rounded-full bg-home-ink/15 dark:bg-white/15" />
            <span className="h-3.5 w-10 rounded-full bg-home-ink/[0.08] dark:bg-white/10" />
          </div>
        </div>
        <span className="absolute -top-3 -right-7 flex h-9 w-9 rotate-[8deg] items-center justify-center rounded-2xl bg-home-yellow shadow-[0_8px_20px_-8px_rgba(33,31,28,0.45)]">
          <AppWindow className="h-4.5 w-4.5 text-home-ink" strokeWidth={2.2} />
        </span>
      </div>

      <h2 className="mt-8 font-display text-[22px] font-semibold tracking-tight text-home-ink dark:text-white">
        {deriveAppDisplayName({ appDir })} isn't here yet
      </h2>
      <p className="mt-2 max-w-sm font-body text-[14px] leading-relaxed text-home-ink/60 dark:text-white/60">
        If your agent is still building it, the app appears here the moment its
        first files land. Otherwise it may have been deleted or renamed.
      </p>

      <button
        type="button"
        onClick={onRefresh}
        className="mt-6 inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-home-ink/15 px-4 font-body text-[13px] font-semibold text-home-ink/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/10"
      >
        <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.2} />
        Check again
      </button>
    </div>
  );
}
